import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  AlertTriangle,
  Building,
  CheckCircle2,
  FileSpreadsheet,
  Phone,
  Receipt,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Empresa } from '@/services/api/empresas';
import { useAuth } from '@/contexts/AuthContext';
import { routes } from '@/lib/routes';
import { cn } from '@/lib/cn';
import {
  InsightsCarousel,
  type InsightsSlide,
} from '@/components/common/InsightsCarousel';
import { EmailQueueHealthBanner } from '@/components/common/EmailQueueHealthBanner';
import { MobileHomeView } from '../components/MobileHomeView';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  RegistrarContactoDialog,
  type AdminLite,
} from '@/modules/facturacion/components/RegistrarContactoDialog';
import {
  getAdminsEnRiesgo,
  getCashFlow,
  getDashboardKpis,
  getDeudaPorPeriodo,
  getFacturacionMensual,
  getFacturacionPendienteMes,
  getMorosidad,
  getTopAdmins,
  getTopMorosos,
  type AdminEnRiesgoRow,
  type CashFlowRow,
  type DashboardKpis,
  type DeudaPorPeriodoRow,
  type FacturacionPendienteMes,
  type MorosidadKpis,
  type NivelRiesgoAdmin,
  type SerieMensual,
  type TopAdminRow,
  type TopMorosoRow,
} from '@/services/api/reportes';

interface LayoutContext {
  empresaActiva: Empresa;
}

export function DashboardPage() {
  const { empresaActiva } = useOutletContext<LayoutContext>();
  const { profile } = useAuth();

  const [kpis, setKpis] = useState<DashboardKpis | null>(null);
  const [serie, setSerie] = useState<SerieMensual[] | null>(null);
  const [topAdmins, setTopAdmins] = useState<TopAdminRow[] | null>(null);
  const [morosidad, setMorosidad] = useState<MorosidadKpis | null>(null);
  const [topMorosos, setTopMorosos] = useState<TopMorosoRow[] | null>(null);
  const [aging, setAging] = useState<DeudaPorPeriodoRow[] | null>(null);
  const [checkMes, setCheckMes] = useState<FacturacionPendienteMes | null>(null);
  const [cashFlow, setCashFlow] = useState<CashFlowRow[] | null>(null);
  const [riesgo, setRiesgo] = useState<AdminEnRiesgoRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [contactoAdmin, setContactoAdmin] = useState<AdminLite | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const [kRes, sRes, tRes, mRes, mmRes, aRes, cRes, fRes, rRes] = await Promise.all([
      getDashboardKpis(empresaActiva.id),
      getFacturacionMensual(empresaActiva.id, 6),
      getTopAdmins(empresaActiva.id, 5),
      getMorosidad(empresaActiva.id),
      getTopMorosos(empresaActiva.id, 5),
      getDeudaPorPeriodo(empresaActiva.id),
      getFacturacionPendienteMes(empresaActiva.id),
      getCashFlow(empresaActiva.id, 6),
      getAdminsEnRiesgo(empresaActiva.id),
    ]);
    setLoading(false);
    if (kRes.ok) setKpis(kRes.data);
    else toast.error(kRes.error.message);
    if (sRes.ok) setSerie(sRes.data);
    if (tRes.ok) setTopAdmins(tRes.data);
    if (mRes.ok) setMorosidad(mRes.data);
    if (mmRes.ok) setTopMorosos(mmRes.data);
    if (aRes.ok) setAging(aRes.data);
    if (cRes.ok) setCheckMes(cRes.data);
    if (fRes.ok) setCashFlow(fRes.data);
    if (rRes.ok) setRiesgo(rRes.data);
  }, [empresaActiva.id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const maxMonto = useMemo(() => {
    if (!serie) return 0;
    return Math.max(1, ...serie.map((s) => s.monto));
  }, [serie]);

  // TODO-MDC-23: en mobile (<md = <768px) se renderiza una vista
  // simplificada con KPIs grandes, avisos y acciones rápidas. La idea
  // explícita del usuario: mobile como centro de notificaciones, los
  // flows complejos (importes, conciliación, plantillas) se hacen en
  // computadora.
  const isMobile = useMediaQuery('(max-width: 767px)');
  const insightsSlides = useMemo(
    () =>
      buildSlides({
        empresaId: empresaActiva.id,
        loading,
        morosidad,
        topMorosos,
        aging,
        serie,
        topAdmins,
        maxMonto,
        cashFlow,
        riesgo,
        onRegistrarContacto: (admin) => setContactoAdmin(admin),
      }),
    [
      empresaActiva.id,
      loading,
      morosidad,
      topMorosos,
      aging,
      serie,
      topAdmins,
      maxMonto,
      cashFlow,
      riesgo,
    ],
  );

  if (isMobile) {
    return (
      <>
        <MobileHomeView
          empresa={empresaActiva}
          fullName={profile?.full_name}
          kpis={kpis}
          morosidad={morosidad}
          insightsSlides={insightsSlides}
        />
        <RegistrarContactoDialog
          open={contactoAdmin !== null}
          onClose={() => setContactoAdmin(null)}
          empresaId={empresaActiva.id}
          admin={contactoAdmin}
          onSaved={() => {
            setContactoAdmin(null);
            void reload();
          }}
        />
      </>
    );
  }

  return (
    <div className="max-w-6xl">
      <header className="mb-5">
        <p className="kicker mb-1.5">Panel principal</p>
        <h1 className="text-3xl font-black tracking-tight text-neutral-ink">
          Hola,{' '}
          <span className="text-blue">
            {firstNameOf(profile?.full_name) || 'MANAXER'}
          </span>
        </h1>
        <p className="mt-1 text-sm text-neutral-muted">
          Operando sobre <b className="text-neutral-ink">{empresaActiva.nombre}</b>.
        </p>
      </header>

      {/* TODO-MDC-04: Health check de la cola de emails. Banner solo
          aparece si la cola está stuck (cron caído) o atrasada. */}
      <EmailQueueHealthBanner empresaId={empresaActiva.id} />

      {/* ── Chequeo mensual: edificios sin facturar este mes ──────────── */}
      {checkMes && <ChequeoMensual data={checkMes} empresaId={empresaActiva.id} />}

      {/* ── KPIs ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricCard
          icon={Users}
          label="Administraciones"
          value={fmtN(kpis?.administraciones_activas)}
          subtext="activas"
          loading={loading}
          to={routes.empresa.clientes.administraciones(empresaActiva.id)}
        />
        <MetricCard
          icon={Building}
          label="Edificios"
          value={fmtN(kpis?.edificios_activos)}
          subtext={
            kpis && kpis.edificios_pausados > 0
              ? `${kpis.edificios_pausados} pausados`
              : 'activos'
          }
          loading={loading}
          to={routes.empresa.clientes.edificios(empresaActiva.id)}
        />
        <MetricCard
          icon={Receipt}
          label="Facturado (mes)"
          value={`$ ${fmtMoney(kpis?.facturado_mes)}`}
          subtext={
            kpis
              ? `${kpis.autorizados_mes} autorizados · ${kpis.borradores_mes} borradores`
              : '—'
          }
          loading={loading}
          to={routes.empresa.facturacion(empresaActiva.id)}
          tone="success"
        />
        <MetricCard
          icon={Wallet}
          label="Saldo cajas"
          value={`$ ${fmtMoney(kpis?.saldo_total)}`}
          subtext={
            kpis && kpis.movimientos_pendientes > 0
              ? `${kpis.movimientos_pendientes} pendientes de ID`
              : 'todo identificado'
          }
          loading={loading}
          to={routes.empresa.finanzas(empresaActiva.id)}
          tone={kpis && kpis.movimientos_pendientes > 0 ? 'warn' : undefined}
        />
      </div>

      {/* ── Insights (carrousel con 3 slides: Cobranza / Antigüedad / Facturación)
             Respeta la regla feedback_page_height: la data importante no se achica
             ni se omite; se agrupa en slides con transición horizontal. */}
      <div className="mt-5">
        <InsightsCarousel
          slides={buildSlides({
            empresaId: empresaActiva.id,
            loading,
            morosidad,
            topMorosos,
            aging,
            serie,
            topAdmins,
            maxMonto,
            cashFlow,
            riesgo,
            onRegistrarContacto: (admin) => setContactoAdmin(admin),
          })}
          height="20rem"
        />
      </div>

      <RegistrarContactoDialog
        open={contactoAdmin !== null}
        onClose={() => setContactoAdmin(null)}
        empresaId={empresaActiva.id}
        admin={contactoAdmin}
        onSaved={() => {
          setContactoAdmin(null);
          void reload();
        }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Slides del carrousel — una función pura que arma las secciones según la data.
// ═══════════════════════════════════════════════════════════════════════════
function buildSlides(params: {
  empresaId: string;
  loading: boolean;
  morosidad: MorosidadKpis | null;
  topMorosos: TopMorosoRow[] | null;
  aging: DeudaPorPeriodoRow[] | null;
  serie: SerieMensual[] | null;
  topAdmins: TopAdminRow[] | null;
  maxMonto: number;
  cashFlow: CashFlowRow[] | null;
  riesgo: AdminEnRiesgoRow[] | null;
  onRegistrarContacto: (admin: AdminLite) => void;
}): InsightsSlide[] {
  const {
    empresaId,
    loading,
    morosidad,
    topMorosos,
    aging,
    serie,
    topAdmins,
    maxMonto,
    cashFlow,
    riesgo,
    onRegistrarContacto,
  } = params;

  return [
    {
      key: 'cobranza',
      kicker: 'Panel de cobranza',
      title: 'Cobranza',
      render: () => (
        <div className="grid h-full gap-3 lg:grid-cols-5">
          <div
            className={cn(
              'card-premium flex flex-col p-4 lg:col-span-2',
              morosidad && morosidad.total_adeudado > 0 && 'ring-1 ring-orange/40',
            )}
          >
            <div className="mb-2 flex items-center justify-between">
              <p className="kicker">Deuda activa</p>
              <AlertTriangle
                size={16}
                className={cn(
                  morosidad && morosidad.total_adeudado > 0
                    ? 'text-orange'
                    : 'text-neutral-muted',
                )}
              />
            </div>
            {loading || !morosidad ? (
              <div className="h-20 animate-pulse rounded bg-surface-bg" />
            ) : morosidad.total_adeudado === 0 ? (
              <EmptyInline msg="Sin deuda activa." />
            ) : (
              <>
                <p className="tabular text-3xl font-black tracking-tight text-orange">
                  $ {fmtMoney(morosidad.total_adeudado)}
                </p>
                <p className="mt-1 text-xs text-neutral-muted">
                  <b className="text-neutral-ink">
                    {fmtN(morosidad.comprobantes_con_deuda)}
                  </b>{' '}
                  comp. ·{' '}
                  <b className="text-neutral-ink">
                    {fmtN(morosidad.edificios_con_deuda)}
                  </b>{' '}
                  edif. ·{' '}
                  <b className="text-neutral-ink">{fmtN(morosidad.admins_con_deuda)}</b>{' '}
                  adm.
                </p>
                <div className="mt-auto border-t border-surface-line/60 pt-2.5">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-muted">
                      Índice
                    </span>
                    <span
                      className={cn(
                        'tabular text-sm font-black',
                        morosidad.morosidad_pct >= 50
                          ? 'text-status-danger'
                          : morosidad.morosidad_pct >= 20
                            ? 'text-orange'
                            : 'text-status-success',
                      )}
                    >
                      {morosidad.morosidad_pct.toFixed(1)}%
                    </span>
                  </div>
                  <p className="mt-0.5 text-[10px] text-neutral-muted">
                    sobre $ {fmtMoney(morosidad.total_facturado_12m)} · últimos 12m
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="card-premium flex flex-col overflow-hidden p-4 lg:col-span-3">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <p className="kicker">Top morosos</p>
                <p className="text-[10px] text-neutral-muted">
                  click al admin para ver sus comprobantes
                </p>
              </div>
              <Users size={16} className="text-orange" />
            </div>
            {loading || !topMorosos ? (
              <RowsSkeleton n={5} />
            ) : topMorosos.length === 0 ? (
              <EmptyInline msg="Todo cobrado." />
            ) : (
              <ul className="space-y-1.5 overflow-y-auto">
                {topMorosos.map((a, i) => (
                  <li
                    key={a.administracion_id}
                    className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-orange/5"
                  >
                    <span className="grid h-6 w-6 flex-none place-items-center rounded-md bg-orange/10 text-[11px] font-bold text-orange">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`${routes.empresa.facturacion(empresaId)}?admin=${a.administracion_id}`}
                        className="truncate text-sm font-semibold text-neutral-ink hover:text-blue"
                      >
                        <code className="font-mono text-[11px] text-neutral-muted">
                          {a.codigo}
                        </code>{' '}
                        {a.nombre}
                      </Link>
                      <p className="truncate text-[10px] text-neutral-muted">
                        {a.edificios_deudores} edif. · {a.comprobantes_adeudados} comp.
                        {a.morosidad_pct > 0 && ` · ${a.morosidad_pct.toFixed(1)}% mora`}
                      </p>
                    </div>
                    <p className="tabular text-sm font-bold text-orange">
                      $ {fmtMoney(a.total_adeudado)}
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        onRegistrarContacto({
                          id: a.administracion_id,
                          codigo: a.codigo,
                          nombre: a.nombre,
                        })
                      }
                      title="Registrar gestión de cobranza"
                      className="grid h-7 w-7 flex-none place-items-center rounded-md text-neutral-muted transition hover:bg-blue-10 hover:text-blue"
                    >
                      <Phone size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'riesgo',
      kicker: 'Admins en riesgo',
      title: 'Riesgo',
      render: () => (
        <div className="flex h-full flex-col">
          <div className="mb-2 flex items-center justify-between">
            <p className="kicker">Deuda + falta de contactos + morosidad alta</p>
            <p className="text-[10px] text-neutral-muted">
              criterios: deuda &gt;$100k, sin contacto &gt;15d, morosidad &gt;20%
            </p>
          </div>
          {loading || !riesgo ? (
            <RowsSkeleton n={5} />
          ) : riesgo.length === 0 ? (
            <EmptyInline msg="Sin admins en riesgo — todo bajo control." />
          ) : (
            <ul className="space-y-1.5 overflow-y-auto">
              {riesgo.slice(0, 5).map((r) => (
                <li
                  key={r.administracion_id}
                  className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-status-danger/5"
                >
                  <RiesgoBadge nivel={r.nivel} criterios={r.criterios_riesgo} />
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`${routes.empresa.facturacion(empresaId)}?admin=${r.administracion_id}`}
                      className="truncate text-sm font-semibold text-neutral-ink hover:text-blue"
                    >
                      <code className="font-mono text-[11px] text-neutral-muted">
                        {r.codigo}
                      </code>{' '}
                      {r.nombre}
                    </Link>
                    <p className="truncate text-[10px] text-neutral-muted">
                      {r.morosidad_pct.toFixed(0)}% mora ·{' '}
                      {r.dias_sin_contacto == null
                        ? 'nunca se contactó'
                        : `${r.dias_sin_contacto}d sin contacto`}
                      {r.promesas_vigentes > 0 && ` · ${r.promesas_vigentes} promesa(s)`}
                    </p>
                  </div>
                  <p className="tabular text-sm font-bold text-status-danger">
                    $ {fmtMoney(r.total_adeudado)}
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      onRegistrarContacto({
                        id: r.administracion_id,
                        codigo: r.codigo,
                        nombre: r.nombre,
                      })
                    }
                    title="Registrar gestión de cobranza"
                    className="grid h-7 w-7 flex-none place-items-center rounded-md text-neutral-muted transition hover:bg-blue-10 hover:text-blue"
                  >
                    <Phone size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ),
    },
    {
      key: 'cashflow',
      kicker: 'Cash flow real vs facturado',
      title: 'Cash flow',
      render: () => (
        <div className="flex h-full flex-col">
          <div className="mb-2 flex items-center justify-between">
            <p className="kicker">
              Flujo mensual: lo que facturaste vs lo que entró a caja
            </p>
            <p className="text-[10px] text-neutral-muted">
              azul = facturado · verde = cobrado · barra naranja abajo = gap
            </p>
          </div>
          {loading || !cashFlow ? (
            <BarsSkeleton />
          ) : cashFlow.every((r) => r.facturado === 0 && r.cobrado === 0) ? (
            <EmptyInline msg="Sin datos de facturación ni cobros en los últimos 6 meses." />
          ) : (
            <CashFlowBars rows={cashFlow} />
          )}
        </div>
      ),
    },
    {
      key: 'aging',
      kicker: 'Antigüedad de la deuda',
      title: 'Antigüedad',
      render: () => (
        <div className="flex h-full flex-col">
          <div className="mb-2 flex items-center justify-between">
            <p className="kicker">$ adeudado por período facturado</p>
            <p className="text-[10px] text-neutral-muted">
              {aging?.length ?? 0} períodos con saldo · de más vieja (izq) a más reciente
              (der)
            </p>
          </div>
          {aging && aging.length > 0 ? (
            <div className="flex-1">
              <AgingBars rows={[...aging].reverse()} />
            </div>
          ) : (
            <EmptyInline msg="No hay deuda pendiente por período." />
          )}
        </div>
      ),
    },
    {
      key: 'facturacion',
      kicker: 'Facturación últimos 6 meses',
      title: 'Facturación',
      render: () => (
        <div className="grid h-full gap-3 lg:grid-cols-5">
          <div className="card-premium flex flex-col overflow-hidden p-4 lg:col-span-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="kicker">Tendencia de facturación</p>
              <TrendingUp size={16} className="text-blue" />
            </div>
            {loading || !serie ? (
              <BarsSkeleton />
            ) : serie.length === 0 ? (
              <EmptyInline msg="Sin comprobantes en los últimos 6 meses." />
            ) : (
              <div className="flex flex-1 items-end gap-3 px-2">
                {serie.map((s) => {
                  const altura = (s.monto / maxMonto) * 100;
                  return (
                    <div key={s.mes} className="flex flex-1 flex-col items-center gap-1">
                      <div className="relative flex h-full w-full items-end">
                        <div
                          className="w-full rounded-t bg-blue/80 transition-all"
                          style={{ height: `${Math.max(4, altura)}%` }}
                          title={`$ ${fmtMoney(s.monto)} · ${s.cantidad} comp.`}
                        />
                      </div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-muted">
                        {labelMes(s.mes)}
                      </p>
                      <p className="tabular text-[10px] text-neutral-ink">
                        $ {fmtMoney(s.monto)}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="card-premium flex flex-col overflow-hidden p-4 lg:col-span-2">
            <div className="mb-2 flex items-center justify-between">
              <p className="kicker">Top del mes</p>
              <Users size={16} className="text-blue" />
            </div>
            {loading || !topAdmins ? (
              <RowsSkeleton n={5} />
            ) : topAdmins.length === 0 ? (
              <EmptyInline msg="Sin facturación." />
            ) : (
              <ul className="space-y-1.5 overflow-y-auto">
                {topAdmins.map((a, i) => (
                  <li
                    key={a.administracion_id}
                    className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-blue-10/40"
                  >
                    <span className="grid h-6 w-6 flex-none place-items-center rounded-md bg-blue-10 text-[11px] font-bold text-blue">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-neutral-ink">
                        <code className="font-mono text-[11px] text-neutral-muted">
                          {a.codigo}
                        </code>{' '}
                        {a.nombre}
                      </p>
                      <p className="truncate text-[10px] text-neutral-muted">
                        {a.edificios} edif. · {a.cantidad} comp.
                      </p>
                    </div>
                    <p className="tabular text-sm font-bold text-neutral-ink">
                      $ {fmtMoney(a.monto)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ),
    },
  ];
}

// ═══════════════════════════════════════════════════════════════════════════

function MetricCard({
  icon: Icon,
  label,
  value,
  subtext,
  loading,
  to,
  tone,
}: {
  icon: typeof TrendingUp;
  label: string;
  value: string;
  subtext?: string;
  loading?: boolean;
  to?: string;
  tone?: 'success' | 'warn';
}) {
  const body = (
    <div
      className={cn(
        'card-interactive p-5',
        tone === 'success' && 'ring-1 ring-status-success/30',
        tone === 'warn' && 'ring-1 ring-orange/30',
      )}
    >
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-muted">
          {label}
        </p>
        <div
          className={cn(
            'grid h-8 w-8 place-items-center rounded-md',
            tone === 'success'
              ? 'bg-status-success/10 text-status-success'
              : tone === 'warn'
                ? 'bg-orange/10 text-orange'
                : 'bg-blue-10 text-blue',
          )}
        >
          <Icon size={16} />
        </div>
      </div>
      {loading ? (
        <div className="h-9 w-24 animate-pulse rounded bg-surface-bg" />
      ) : (
        <p className="tabular text-3xl font-black tracking-tight">{value}</p>
      )}
      {subtext && <p className="mt-1 text-xs text-neutral-muted">{subtext}</p>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function RiesgoBadge({
  nivel,
  criterios,
}: {
  nivel: NivelRiesgoAdmin;
  criterios: number;
}) {
  const cfg = {
    ok: { bg: 'bg-status-success/15', fg: 'text-status-success', label: 'OK' },
    bajo: { bg: 'bg-blue-10', fg: 'text-blue', label: 'Bajo' },
    medio: { bg: 'bg-orange/15', fg: 'text-orange', label: 'Medio' },
    alto: { bg: 'bg-status-danger/15', fg: 'text-status-danger', label: 'Alto' },
  }[nivel];
  return (
    <span
      className={cn(
        'grid h-6 min-w-[28px] flex-none place-items-center rounded-md px-1.5 text-[10px] font-black uppercase tracking-wider',
        cfg.bg,
        cfg.fg,
      )}
      title={`${criterios}/3 criterios de riesgo disparados`}
    >
      {cfg.label}
    </span>
  );
}

/**
 * Semáforo de facturación del mes: cuántos edificios activos todavía no
 * tienen comprobante en el período actual. Verde si completo, ámbar si
 * faltan pocos, rojo si faltan muchos o si estamos pasado día 15 y todavía
 * quedan varios sin facturar.
 */
function ChequeoMensual({
  data,
  empresaId,
}: {
  data: FacturacionPendienteMes;
  empresaId: string;
}) {
  const {
    edificios_activos: total,
    edificios_facturados: ok,
    edificios_pendientes: pend,
    admins_con_pendientes: adms,
    monto_estimado_pendiente: monto,
  } = data;

  const completo = pend === 0 && total > 0;
  const diaActual = new Date().getDate();
  const urgente = pend > 0 && diaActual >= 15; // mitad de mes + faltan edificios

  if (total === 0) {
    return null; // empresa sin edificios activos: no tiene sentido mostrar
  }

  const pctFacturado = Math.round((ok / total) * 100);
  const tone = completo ? 'success' : urgente ? 'danger' : 'warn';
  const mesNombre = labelMesLargo(data.periodo);

  return (
    <div
      className={cn(
        'card-premium mb-5 flex items-center gap-4 p-4',
        tone === 'success' && 'bg-status-success/5 ring-1 ring-status-success/30',
        tone === 'warn' && 'bg-orange/5 ring-1 ring-orange/40',
        tone === 'danger' && 'bg-status-danger/5 ring-1 ring-status-danger/40',
      )}
    >
      <div
        className={cn(
          'grid h-12 w-12 flex-none place-items-center rounded-md',
          tone === 'success'
            ? 'bg-status-success/10 text-status-success'
            : tone === 'warn'
              ? 'bg-orange/10 text-orange'
              : 'bg-status-danger/10 text-status-danger',
        )}
      >
        {completo ? <CheckCircle2 size={22} /> : <AlertTriangle size={22} />}
      </div>

      <div className="min-w-0 flex-1">
        <p className="kicker mb-0.5">Facturación del mes · {mesNombre}</p>
        {completo ? (
          <p className="text-sm font-semibold text-neutral-ink">
            Todos los edificios activos ({ok}) ya tienen comprobante este mes. 👌
          </p>
        ) : (
          <p className="text-sm text-neutral-ink">
            Te faltan facturar{' '}
            <b className={tone === 'danger' ? 'text-status-danger' : 'text-orange'}>
              {pend} edificios
            </b>{' '}
            de {total} activos
            {adms > 0 && (
              <>
                {' '}
                (en <b>{adms}</b> administracion{adms > 1 ? 'es' : ''})
              </>
            )}
            .{' '}
            {monto > 0 && (
              <span className="text-neutral-muted">
                Monto estimado pendiente:{' '}
                <b className="text-neutral-ink">$ {fmtMoney(monto)}</b>
              </span>
            )}
          </p>
        )}
        <p className="mt-1 text-[11px] text-neutral-muted">
          {pctFacturado}% completo ·{' '}
          {urgente
            ? 'Ya pasó mitad de mes — revisar pendientes cuanto antes.'
            : completo
              ? 'Listo para cerrar el lote mensual.'
              : 'A tiempo — seguí facturando.'}
        </p>
      </div>

      {!completo && (
        <Link
          to={routes.empresa.facturacion(empresaId)}
          className={cn(
            'rounded-md px-3 py-1.5 text-sm font-semibold transition',
            tone === 'danger'
              ? 'bg-status-danger text-white hover:bg-status-danger/90'
              : 'bg-orange text-white hover:bg-orange/90',
          )}
        >
          Ir a Facturación →
        </Link>
      )}
    </div>
  );
}

function labelMesLargo(iso: string): string {
  const [y, m] = iso.split('-');
  const meses = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ];
  const idx = Math.max(0, Math.min(11, Number(m) - 1));
  return `${meses[idx]} ${y}`;
}

/**
 * Barras pares mensuales: facturado (azul) al lado de cobrado (verde), y
 * abajo una barra estrecha naranja que muestra el gap cuando cobrado es
 * menor que facturado (o verde cuando cobrado supera a facturado, señal
 * de recupero de deuda vieja). Hover muestra $ exactos y %.
 */
function CashFlowBars({ rows }: { rows: CashFlowRow[] }) {
  const maxBar = Math.max(1, ...rows.flatMap((r) => [r.facturado, r.cobrado]));
  const totalFact = rows.reduce((a, r) => a + r.facturado, 0);
  const totalCob = rows.reduce((a, r) => a + r.cobrado, 0);
  const tasa = totalFact > 0 ? (totalCob / totalFact) * 100 : 0;

  return (
    <>
      <div className="flex h-full flex-col">
        <div className="flex flex-1 items-end gap-3 px-1">
          {rows.map((r) => {
            const hFact = (r.facturado / maxBar) * 100;
            const hCob = (r.cobrado / maxBar) * 100;
            const recupero = r.gap < 0;
            const ratio = r.facturado > 0 ? (r.cobrado / r.facturado) * 100 : 0;
            return (
              <div key={r.mes} className="flex flex-1 flex-col items-center gap-1">
                <div className="relative flex h-full w-full items-end gap-0.5">
                  <div className="flex h-full flex-1 items-end">
                    <div
                      className="w-full rounded-t bg-blue/80 transition-all"
                      style={{ height: `${Math.max(3, hFact)}%` }}
                      title={`Facturado: $ ${fmtMoney(r.facturado)}`}
                    />
                  </div>
                  <div className="flex h-full flex-1 items-end">
                    <div
                      className={cn(
                        'w-full rounded-t transition-all',
                        recupero ? 'bg-status-success' : 'bg-status-success/70',
                      )}
                      style={{ height: `${Math.max(3, hCob)}%` }}
                      title={`Cobrado: $ ${fmtMoney(r.cobrado)} (${ratio.toFixed(0)}% del facturado)`}
                    />
                  </div>
                </div>
                <p className="text-[9px] font-semibold uppercase tracking-wider text-neutral-muted">
                  {labelMes(r.mes)}
                </p>
                <p
                  className={cn(
                    'tabular text-[10px] font-semibold',
                    recupero
                      ? 'text-status-success'
                      : r.gap > 0
                        ? 'text-orange'
                        : 'text-neutral-muted',
                  )}
                  title={
                    recupero
                      ? `Recuperaste $ ${fmtMoney(-r.gap)} de deuda vieja este mes`
                      : `Gap de cobro: $ ${fmtMoney(r.gap)}`
                  }
                >
                  {recupero
                    ? `+${fmtMoney(-r.gap)}`
                    : r.gap === 0
                      ? '—'
                      : `−${fmtMoney(r.gap)}`}
                </p>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 border-t border-surface-line/60 pt-3 text-[11px]">
        <div className="flex items-center gap-3 text-neutral-muted">
          <LegendDot className="bg-blue/80" /> Facturado
          <LegendDot className="bg-status-success/70" /> Cobrado
        </div>
        <p className="tabular font-semibold text-neutral-ink">
          Tasa cobro 6m:{' '}
          <span
            className={cn(
              tasa >= 95
                ? 'text-status-success'
                : tasa >= 70
                  ? 'text-blue'
                  : 'text-orange',
            )}
          >
            {tasa.toFixed(1)}%
          </span>{' '}
          · $ {fmtMoney(totalCob)} de $ {fmtMoney(totalFact)}
        </p>
      </div>
    </>
  );
}

/**
 * Barras verticales por período con $ adeudado. Los períodos más antiguos
 * se tiñen rojo (urgente, ya son mora dura), los recientes amarillo/verde.
 * Cada barra es clickeable y lleva a Facturación filtrado por ese período
 * con soloPendiente=true.
 */
function AgingBars({ rows }: { rows: DeudaPorPeriodoRow[] }) {
  const hoyYYYYMM = new Date().toISOString().slice(0, 7);
  const maxMonto = Math.max(1, ...rows.map((r) => r.adeudado));
  const total = rows.reduce((a, r) => a + r.adeudado, 0);

  // Bucket de antigüedad en meses.
  function mesesDeAntiguedad(periodoIso: string): number {
    const [y, m] = periodoIso.split('-');
    const [hy, hm] = hoyYYYYMM.split('-');
    return (Number(hy) - Number(y)) * 12 + (Number(hm) - Number(m));
  }
  function colorDe(meses: number): string {
    if (meses <= 0) return 'bg-status-success/70';
    if (meses <= 2) return 'bg-blue/70';
    if (meses <= 5) return 'bg-orange/75';
    if (meses <= 11) return 'bg-status-danger/75';
    return 'bg-status-danger'; // > 11 meses = deuda muy vieja
  }

  return (
    <>
      <div className="flex h-40 items-end gap-2 px-1">
        {rows.map((r) => {
          const meses = mesesDeAntiguedad(r.periodo);
          const altura = (r.adeudado / maxMonto) * 100;
          const pct = total > 0 ? (r.adeudado / total) * 100 : 0;
          return (
            <div key={r.periodo} className="flex flex-1 flex-col items-center gap-1">
              <div className="relative flex h-full w-full items-end">
                <div
                  className={cn('w-full rounded-t transition-all', colorDe(meses))}
                  style={{ height: `${Math.max(3, altura)}%` }}
                  title={`${labelMes(r.periodo)} · $ ${fmtMoney(r.adeudado)} · ${r.comprobantes} comp. · ${meses} meses · ${pct.toFixed(1)}% del total`}
                />
              </div>
              <p className="text-[9px] font-semibold uppercase tracking-wider text-neutral-muted">
                {labelMes(r.periodo)}
              </p>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 border-t border-surface-line/60 pt-3 text-[11px]">
        <div className="flex items-center gap-3 text-neutral-muted">
          <LegendDot className="bg-status-success/70" /> mes actual
          <LegendDot className="bg-blue/70" /> 1–2 meses
          <LegendDot className="bg-orange/75" /> 3–5 meses
          <LegendDot className="bg-status-danger/75" /> 6–11 meses
          <LegendDot className="bg-status-danger" /> 12+ meses
        </div>
        <p className="tabular font-semibold text-neutral-ink">
          Total deuda: $ {fmtMoney(total)}
        </p>
      </div>
    </>
  );
}

function LegendDot({ className }: { className: string }) {
  return <span className={cn('mr-1 inline-block h-2 w-2 rounded-sm', className)} />;
}

function BarsSkeleton() {
  return (
    <div className="flex h-56 items-end gap-3 px-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex-1 animate-pulse rounded-t bg-surface-bg"
          style={{ height: `${40 + ((i * 10) % 60)}%` }}
        />
      ))}
    </div>
  );
}
function RowsSkeleton({ n }: { n: number }) {
  return (
    <ul className="space-y-2.5">
      {Array.from({ length: n }).map((_, i) => (
        <li key={i} className="h-12 animate-pulse rounded-md bg-surface-bg" />
      ))}
    </ul>
  );
}
function EmptyInline({ msg }: { msg: string }) {
  return (
    <div className="grid h-40 place-items-center text-center text-xs text-neutral-muted">
      <span>
        <FileSpreadsheet size={20} className="mx-auto mb-1 text-neutral-muted/60" />
        {msg}
      </span>
    </div>
  );
}

function fmtN(n: number | undefined): string {
  if (n === undefined || n === null) return '—';
  return new Intl.NumberFormat('es-AR').format(n);
}
function fmtMoney(n: number | undefined): string {
  if (n === undefined || n === null) return '—';
  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}
function labelMes(iso: string): string {
  const [y, m] = iso.split('-');
  const meses = [
    'ene',
    'feb',
    'mar',
    'abr',
    'may',
    'jun',
    'jul',
    'ago',
    'sep',
    'oct',
    'nov',
    'dic',
  ];
  const idx = Math.max(0, Math.min(11, Number(m) - 1));
  return `${meses[idx]} ${y?.slice(2)}`;
}
function firstNameOf(fullName: string | null | undefined): string | null {
  if (!fullName) return null;
  return fullName.trim().split(/\s+/)[0] ?? null;
}
